import React, { useState } from 'react';

import Tooltip from 'components/common/Tooltip';
import useMartyContext from 'hooks/useMartyContext';
import type { SocialIconProduct } from 'reducers/detail/productDetail';
import FacebookShareV2 from 'components/productdetail/sharing/FacebookShareV2';
import LinkShare from 'components/productdetail/sharing/LinkShare';
import PinterestShareV2 from 'components/productdetail/sharing/PinterestShareV2';
import TwitterShareV2 from 'components/productdetail/sharing/TwitterShareV2';
import ProductUtils from 'helpers/ProductUtils';
import UtilityStrokeShareMediumIcon from 'tailwind/components/Icons/UtilityStrokeShareMediumIcon';
import ShareIcon from 'components/common/IconsV2/ShareIcon';
import { userAgentStringToPlatform } from 'helpers/UserAgentUtils';
import { cn } from 'helpers/classnames';

import css from 'styles/components/common/shareSocialLinks.scss';

export interface ShareSocialLinksProps {
  productDetail: SocialIconProduct;
  onSharingButtonClick: (linkName: string) => void;
  wrapperClassName?: string;
  mobileWrapperClassName?: string;
  isOneOfPdpEnhancementsTreatmentEnabled?: boolean;
}

const ShareSocialLinks = (props: ShareSocialLinksProps) => {
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const [isContentVisible, setIsContentVisible] = useState(false);

  const {
    productDetail: { fbAppId, linksProductContent, productId, styleId },
    onSharingButtonClick,
    wrapperClassName,
    mobileWrapperClassName,
    isOneOfPdpEnhancementsTreatmentEnabled
  } = props;
  const { testId } = useMartyContext();

  const showLinkClicked = () => {
    setIsLinkCopied(true);
    setTimeout(() => setIsLinkCopied(false), 2000);
  };

  const onShareSocialClick = (toolTipOnClick: () => void) => {
    const platform = userAgentStringToPlatform();
    if (navigator.share && (platform === 'android' || platform === 'iphone' || platform === 'ipad')) {
      navigator.share({
        url: `${linksProductContent.link}`
      });
    } else {
      toolTipOnClick();
    }
    !isContentVisible && ProductUtils.generateShareIconClickEvent(productId, styleId);
    setIsContentVisible(true);
  };

  const standardClasses = cn(wrapperClassName, 'lumon:hover:border-gray-800 lumon:hover:text-gray-800');
  // 6pm needs the important modifiers to work
  const pdpEnhancementsClasses = cn(
    'bg-default-minimal p-1.5 rounded-full border border-button-moderate border-solid text-primary hover:border-active hover:text-accent lg:!hidden',
    'lumon:hover:border-gray-800 lumon:hover:text-gray-800'
  );

  return (
    <Tooltip
      direction="bottom"
      wrapperClassName={isOneOfPdpEnhancementsTreatmentEnabled ? pdpEnhancementsClasses : standardClasses}
      // this before adds an invisible rectangle shaped content covering the tooltip button when the tooltip opens to prevent it from hiding while leaving the button
      contentClassName="!p-0 before:content-[''] before:absolute before:top-[-20px] before:left-1/2 before:-translate-x-1/2 before:w-0 before:h-0 before:border-l-[20px] before:border-r-[20px] before:border-b-[20px] before:border-[transparent] before:-z-10"
      tooltipClassName={css.tooltip}
      mobileWrapper={mobileWrapperClassName}
      ariaLabel="Social Media Share Icon, (opens drop down)"
      dataTestId="shareIconButton"
      clickToOpen={true}
      onClick={onShareSocialClick}
      isLinkCopied={isLinkCopied}
      setIsContentVisible={setIsContentVisible}
      content={
        <div className={css.sharingV2}>
          <div className={css.sharingIconsV2} data-test-id={testId('shareSectionV2')}>
            <ul>
              <li className={css.links}>
                <FacebookShareV2
                  className={css.facebookV2}
                  appId={fbAppId}
                  product={linksProductContent}
                  onClick={() => onSharingButtonClick('facebook')}
                  iconFillColor={'#4267B2'}
                  iconName="Facebook"
                />
              </li>
              <li className={css.links}>
                <TwitterShareV2
                  className={css.twitterV2}
                  product={linksProductContent}
                  onClick={() => onSharingButtonClick('twitter')}
                  iconFillColor={'#1DA1F2'}
                  iconName="Twitter"
                />
              </li>
              <li className={css.links}>
                <PinterestShareV2
                  className={css.pinterestV2}
                  product={linksProductContent}
                  onClick={() => onSharingButtonClick('pinterest')}
                  iconFillColor={'#E60023'}
                  iconName="Pinterest"
                />
              </li>
              <li className={css.links}>
                <LinkShare
                  className={css.link}
                  product={linksProductContent}
                  onClick={() => onSharingButtonClick('copy')}
                  showLinkClicked={showLinkClicked}
                  isLinkCopied={isLinkCopied}
                  iconFillColor={'#888'}
                  iconName="Copy Link"
                />
              </li>
            </ul>
          </div>
        </div>
      }
    >
      {/* @ts-ignore */}
      <UtilityStrokeShareMediumIcon size={20} className="lumon:hidden" />
      <ShareIcon size={24} className="size-5 not-lumon:hidden" />
    </Tooltip>
  );
};

export default ShareSocialLinks;
